import React, { useMemo, useState } from "react";
// Removed old containers
// import ProgramsWithResearchersTable from "../../components/dashboard/admin/ProgramWithResearchersTable";
// import PentestersLeaderboard from "../../components/dashboard/admin/PentestersLeaderboard";
// import RetestReportsListTable from "../../components/dashboard/admin/RetestReportsListTable";
// import UnderReviewReportsListTable from "../../components/dashboard/admin/UnderReviewReportsListTable";
import useAdminDashboardDetails from "../../utils/hooks/dashboard/useAdminDashboardDetails";
import usePageTitle from "../../utils/hooks/usePageTitle";
// import ResearcherPieChart from "../../components/dashboard/admin/ResearcherPieChart";
// import RetestBarChart from "../../components/dashboard/admin/RetestBarChart";
// import ProgramGrowthChart from "../../components/dashboard/admin/ProgramGrowthChart";
// import GroupedSeverityChart from "../../components/dashboard/admin/GroupedSeverityChart"; // Import the new component
import TopSummaryCards from "../../components/dashboard/admin/TopSummaryCards";
import ActionRequiredList from "../../components/dashboard/admin/ActionRequiredList";
import VulnerabilityTrendsChart from "../../components/dashboard/admin/VulnerabilityTrendsChart";
import ProgramStatusOverviewChart from "../../components/dashboard/admin/ProgramStatusOverviewChart";
import ActionableTabs from "../../components/dashboard/admin/ActionableTabs";
import Leaderboard from "../researcher/Leaderboard";
import UpcomingProgramsCalendar from "../../components/dashboard/admin/UpcomingProgramsCalendar";

const AdminDashboard = () => {
	usePageTitle("Dashboard");
	const { details, loading, error } = useAdminDashboardDetails();
	const [selectedResearcherId, setSelectedResearcherId] = useState<number | "all">("all");

	const helpers = useMemo(() => {
		const activeThisWeek = details?.activeProgramsActivatedThisWeek ?? 0;
		const researchersThisMonth = details?.activeResearchersAddedThisMonth ?? 0;
		return {
			activePrograms: `+${activeThisWeek} this week`,
			pendingReports: "Awaiting triage",
			pendingRetests: "Action required",
			activeResearchers: `+${researchersThisMonth} this month`
		} as const;
	}, [details]);

	const slaView = useMemo(() => {
		if (!details) {
			return {
				researcherOptions: [] as Array<{ id: number; name: string; email: string | null }>,
				filteredBreaches: [] as any[],
				filteredHistory: [] as any[],
				totalPenalty: 0,
				totalBreaches: 0
			};
		}

		const allBreaches = details.slaBreaches ?? [];
		const allHistory = details.slaBreachHistory ?? [];

		const researcherOptions = allBreaches.map(item => ({
			id: item.userId,
			name: item.name,
			email: item.email ?? null
		}));

		const filteredBreaches =
			selectedResearcherId === "all"
				? allBreaches
				: allBreaches.filter(item => item.userId === selectedResearcherId);

		const filteredHistory =
			selectedResearcherId === "all"
				? allHistory
				: allHistory.filter(item => item.researcherId === selectedResearcherId);

		const totalPenalty = filteredHistory.reduce(
			(sum, item) => sum + (item.penaltyPoints || 0),
			0
		);
		const totalBreaches = filteredHistory.length;

		return { researcherOptions, filteredBreaches, filteredHistory, totalPenalty, totalBreaches };
	}, [details, selectedResearcherId]);

	if (loading) {
		return (
			<div className="montserrat-font min-h-screen bg-slate-100 p-6 text-center text-lg font-semibold">
				Loading..
			</div>
		);
	}

	if (error) {
		return (
			<div className="montserrat-font min-h-screen bg-slate-100 p-6 text-center text-red-600">
				Error: {error}
			</div>
		);
	}

	const hasAnySlaData =
		(details?.slaBreaches && details.slaBreaches.length > 0) ||
		(details?.slaBreachHistory && details.slaBreachHistory.length > 0);

	return (
		<div className="montserrat-font min-h-screen bg-slate-100 px-2 py-4">
			{details && (
				<>
					{/* New top four summary cards */}
					<TopSummaryCards
						activePrograms={details.activePrograms}
						pendingReports={details.underReviewReports}
						pendingRetests={details.totalRetestReports}
						activeResearchers={details.activeResearchers}
						helpers={helpers}
					/>

					<div className="mt-4 flex w-full gap-4 px-4">
						<div className="w-1/2">
							<VulnerabilityTrendsChart
								buckets={details.vulnerabilityTrends?.buckets}
								buckets7d={details.vulnerabilityTrends7d?.buckets}
							/>
						</div>
						<div className="w-1/2">
							<ActionRequiredList items={details.actionRequiredPrograms} />
						</div>
					</div>

					{/* New row: Program Status Overview graph (last 30 days) */}
					<div className="mt-4 flex w-full gap-4 px-4">
						<div className="w-1/2">
							<ProgramStatusOverviewChart
								series={details.programStatusOverview?.series}
								totals={details.programStatusOverview?.totals}
								series7d={details.programStatusOverview7d?.series}
								totals7d={details.programStatusOverview7d?.totals}
							/>
						</div>
						<div className="w-1/2">
							<ActionableTabs
								reports={details.underReviewReportsList || []}
								retests={details.retestReportsList || []}
							/>
						</div>
					</div>

					{/* Removed old analytics and tables */}

					{/* Upcoming Programs Calendar */}
					<div className="mt-4 px-4">
						<UpcomingProgramsCalendar items={details.upcomingUnassignedPrograms} />
					</div>

					{/* Retest SLA breaches - combined view with filters */}
					{hasAnySlaData && (
						<div className="mt-4 px-4">
							<div className="rounded-2xl border border-red-100 bg-gradient-to-r from-red-50 via-rose-50 to-white p-6 shadow-sm">
								<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
									<div className="flex items-start gap-3">
										<div className="mt-1 flex h-9 w-9 items-center justify-center rounded-full bg-red-100 text-red-600">
											<svg
												viewBox="0 0 24 24"
												className="h-4 w-4"
												fill="none"
												stroke="currentColor"
												strokeWidth="2"
											>
												<path d="M12 9v4" strokeLinecap="round" strokeLinejoin="round" />
												<path d="M12 17h.01" strokeLinecap="round" strokeLinejoin="round" />
												<path
													d="M10.29 3.86 2.82 17.02A1.6 1.6 0 0 0 4.21 19.4h15.58a1.6 1.6 0 0 0 1.39-2.38L13.71 3.86a1.6 1.6 0 0 0-2.82 0Z"
													strokeLinecap="round"
													strokeLinejoin="round"
												/>
											</svg>
										</div>
										<div>
											<h3 className="text-sm font-semibold text-[#7F1D1D]">Retest SLA Breaches</h3>
											<p className="mt-1 text-xs text-red-900/80">
												Overview of researchers who breached the 24h retest SLA and the
												specific retests that triggered reputation penalties.
											</p>
										</div>
									</div>
									<div className="text-right">
										<p className="text-[10px] font-semibold uppercase tracking-wide text-red-900/70">
											Total SLA penalty issued
										</p>
										<p className="mt-1 text-base font-semibold text-red-700">
											-{slaView.totalPenalty} pts
										</p>
										<p className="mt-1 text-[11px] text-gray-600">
											{slaView.totalBreaches} breach
											{slaView.totalBreaches !== 1 ? "es" : ""} in last {details.slaLookbackDays ?? 90} days
										</p>
									</div>
								</div>

								{slaView.researcherOptions.length > 0 && (
									<div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[11px] text-gray-600">
										<div className="flex items-center gap-2">
											<span className="font-medium text-gray-700">Filter by researcher</span>
											<select
												className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 shadow-sm focus:border-red-300 focus:outline-none focus:ring-1 focus:ring-red-200"
												value={selectedResearcherId === "all" ? "all" : String(selectedResearcherId)}
												onChange={e =>
													setSelectedResearcherId(
														e.target.value === "all" ? "all" : Number(e.target.value)
													)
												}
											>
												<option value="all">All researchers</option>
												{slaView.researcherOptions.map(option => (
													<option key={option.id} value={option.id}>
														{option.name}
														{option.email ? ` · ${option.email}` : ""}
													</option>
												))}
											</select>
										</div>
										<span className="text-xs text-gray-500">
											Last {details.slaLookbackDays ?? 90} days
										</span>
									</div>
								)}

								{slaView.filteredBreaches.length > 0 && (
									<div className="mt-4 rounded-xl bg-white/90 p-4 shadow-sm ring-1 ring-gray-100">
										<div className="mb-3 flex items-center justify-between">
											<h4 className="text-sm font-semibold text-[#010D2C]">
												Researchers with SLA breaches
											</h4>
										</div>
										<div className="overflow-x-auto">
											<table className="min-w-full text-left text-sm">
												<thead>
													<tr className="text-xs font-semibold uppercase tracking-wide text-gray-500">
														<th className="pb-2 pr-4">Researcher</th>
														<th className="pb-2 pr-4">Email</th>
														<th className="pb-2 pr-4">SLA Breaches</th>
														<th className="pb-2 pr-4">Penalty Points</th>
														<th className="pb-2 pr-4">Last Breach</th>
													</tr>
												</thead>
												<tbody>
													{slaView.filteredBreaches.map((item: any) => (
														<tr
															key={item.userId}
															className="border-t border-gray-100 last:border-b-0"
														>
															<td className="py-2 pr-4">
																<a
																	href={`/dashboard/users/researcher-profile/${item.userId}`}
																	className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
																>
																	{item.name}
																</a>
															</td>
															<td className="py-2 pr-4 text-sm text-gray-500">
																{item.email || "—"}
															</td>
															<td className="py-2 pr-4 text-sm font-semibold text-gray-900">
																{item.totalBreaches}
															</td>
															<td className="py-2 pr-4 text-sm text-gray-900">
																{item.penaltyPoints}
															</td>
															<td className="py-2 pr-4 text-sm text-gray-500">
																{item.lastBreachAt || "—"}
															</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
									</div>
								)}

								{slaView.filteredHistory.length > 0 && (
									<>
										<div className="mt-4 mb-1 text-xs font-medium uppercase tracking-wide text-red-900/80">
											Latest SLA-breached retests
										</div>
										<div className="mt-1 divide-y divide-red-100/70 rounded-xl bg-white/80">
											{slaView.filteredHistory.slice(0, 7).map((item: any) => {
												const severityLabel = (item.severityCategory || "").trim();
												const lower = severityLabel.toLowerCase();
												const severityColor =
													lower === "critical"
														? "bg-red-100 text-red-700"
														: lower === "high"
															? "bg-orange-100 text-orange-700"
															: lower === "medium"
																? "bg-amber-100 text-amber-700"
																: lower === "low"
																	? "bg-emerald-100 text-emerald-700"
																	: (lower === "info" || lower === "informational")
																		? "bg-blue-100 text-blue-700"
																		: "bg-gray-100 text-gray-700";

												return (
													<div
														key={`${item.retestId}-${item.reportId}`}
														className="flex items-center justify-between px-3 py-2.5 text-xs"
													>
														<div className="min-w-0">
															<p className="truncate font-medium text-gray-900">
																#{item.reportId}
																{" \u00b7 "}
																{item.reportTitle || "Retest"}
															</p>
															<div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-gray-500">
																<span className="truncate max-w-[160px]">
																	{item.programTitle || "Program"}
																</span>
																{severityLabel && (
																	<span
																		className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${severityColor}`}
																	>
																		{severityLabel.toLowerCase() === "none" ? "Info" : severityLabel}
																	</span>
																)}
																<span className="truncate max-w-[180px] text-[10px] text-gray-400">
																	Researcher:{" "}
																	<a
																		href={`/dashboard/users/researcher-profile/${item.researcherId}`}
																		className="font-medium text-indigo-600 hover:text-indigo-800"
																	>
																		{item.researcherName}
																	</a>
																	{item.researcherEmail && ` · ${item.researcherEmail}`}
																</span>
																<span className="text-[10px] text-gray-400">
																	Breached on {item.slaBreachedAt}
																</span>
															</div>
														</div>
														<div className="ml-3 text-right">
															<p className="text-[11px] font-semibold text-red-600">
																-{item.penaltyPoints} pts
															</p>
															<a
																href={`/dashboard/retests/${item.retestId}`}
																className="mt-1 inline-flex items-center rounded-full border border-gray-200 px-3 py-0.5 text-[10px] font-medium text-gray-700 hover:border-red-200 hover:bg-red-50/60 hover:text-red-700"
															>
																View retest
															</a>
														</div>
													</div>
												);
											})}
										</div>
										{slaView.filteredHistory.length > 7 && (
											<p className="mt-3 text-[11px] text-gray-500">
												Showing latest 7 of {slaView.filteredHistory.length} SLA-breached retests.
											</p>
										)}
									</>
								)}
							</div>
						</div>
					)}

					{/* Leaderboard - reuse researcher leaderboard at bottom */}
					<div className="mt-4 px-4 rounded-2xl bg-white p-0 shadow-lg ring-1 ring-gray-100">
						<Leaderboard viewProfilePath={(userId: number) => `/dashboard/users/researcher-profile/${userId}`} />
					</div>

					{/* Removed legacy charts and tables */}
				</>
			)}
		</div>
	);
};

export default AdminDashboard;